import { CurrencyEnum } from '../../enums';
import { GenericStatusByCall } from '../../enums/status-by-call';
import { IExecution } from '../../models/execution';
import {
  SORExtendedHoursMarker,
  SOROrderSide,
  SOROrderStatus,
  SOROrderTimeInForce,
  SOROrderType,
  TenNSOROrderStatus,
} from '../../models/gateway/types';
import { FxTransactionResponse, PeopleAlsoTradeItem, ReportingCall } from '../../models/reporting/types';
import { NullableNumber, NullableString } from '../../util/types';
import { CommonPayloadWithCaller } from '../common-actions';
import { SubscribeGroup } from '../market-data/types';

export type GetMyAccountChartDataPayload = {
  accountReferenceId: string;
  fromDate: string;
  toDate: string;
};

export type NetIncomeComponentsChartData = {
  date: string;
  netEquity: number;
  pnlResult: number;
};

export interface AccountReferenceAndSummary {
    accountReferenceId: string;
    accountSummary: AccountSummaryResponseData[];
}

export interface AccountReferenceAndSummaryResponse {
    accounts: AccountReferenceAndSummary[];
    message: NullableString;
    resultCode: number;
    messageType: NullableString;
    timeStamp: NullableString;
    traceIdentifier: NullableString;
    userId: NullableString;
}

export type AccountSummaryPayloadData = {
  accountReferenceId: string;
};

export type OrderDetailPayloadData = AccountSummaryPayloadData & {
  parentOrderId: string;
};

export type OpenPositionsPayloadData = {
  accountReferenceId?: string;
  accountsList?: string[];
  symbol?: string,
  positionType?: string,
  group?: SubscribeGroup
} | undefined;

export type MyAccountChartDataResponse = {
  date: string;
  netEquity: number;
  pnlResult: number;
};

export type AccountSummaryResponseData = {
  accountReferenceId: string;
  currency: string;
  cashBalance: number;
  tradingCharges: number;
  accountCharges: number;
  realizedPnL: number;
  realTimeCash: number;
  hardBPFactorID: number;
  hardBPFactorON: number;
  netCash: number;
  retailCash: number;
};

export type OpenPosition = {
  accountReferenceId: string;
  symbol: string;
  currency: string;
  positionType: string;
  lastExecutionDateTime: string;
  quantity: number;
  averageOpeningPrice: number;
  cost: number;
  realizedPnl?: number;
};

export type ReportingOrder = {
  symbol: string;
  parentOrderId: string;
  totalQuantity: number;
  sorExecutedQuantity: number;
  aqxExecutedQuantity: number;
  averageFilledPrice: number;
  orderRequestedPrice: number;
  type: SOROrderType;
  side: SOROrderSide;
  orderDate: string;
  timeInForce: SOROrderTimeInForce;
  expirationDate: string;
};

export type OrderDetailResponse = ReportingOrder & {
  status: SOROrderStatus;
  stopPrice: number;
  fillDate: string;
  lastUpdatedDateTime: string;
  orderValue: number;
  grossRealizedPnl: number;
  regulatoryFees: number;
  tradingSessionId?: string;
  taf?: number;
  sec?: number;
};

export type AccountTradeHistoryPayloadData = {
  accountsList?: string[];
  fromDate?: string;
  toDate?: string;
  symbols_StringList?: string;
  statuses_StringList?: string;
  tenNStatuses_StringList?: string;
  pageNumber?: number;
  rowsPerPage?: number;
  isPendingOrdersCall?: boolean;
  isSymbolDetails?: boolean;
} & CommonPayloadWithCaller;

export type FxTransactionPayloadData = {
  userId?: string;
  messageType: string;
  traceIdentifier?: string;
  timeStamp?: string;
  symbolsList?: string;
  fromDate: string;
  toDate: string;
  pageNumber: number;
  rowsPerPage: number;
  tenNStatusList: string;
  accountsList: string[];
};

export type AccountTradeHistoryResponseData = ReportingOrder & {
  pages: number;
  rowNumber: number;
  orderStopPrice: number;
  aqxLastExecutionDate: string;
  lastUpdatedDate: string;
  status: SOROrderStatus;
  tenNStatus: TenNSOROrderStatus;
  tradingSessionId: SORExtendedHoursMarker;
};

export type AccountsTradeHistory = {
  accountReferenceId: string;
  ordersList: AccountTradeHistoryResponseData[];
}

export type AccountTradeHistoryResponseDataWithCustomData = {
  accounts: AccountsTradeHistory[];
  customData?: AccountTradeHistoryCustomData;
};

export type AccountTradeHistoryCustomData = {
  isSymbolDetails?: boolean;
  queueLength?: number;
  isPendingOrdersCall?: boolean;
}

export enum PositionType {
  Buy = 'Buy',
  Sell = 'Sell',
}

export enum TimeInForce {
  Day,
  GTC,
  AtOpening,
  IOC,
  FOK,
  ExtendedDay,
  GoodTillDate,
  AtClosing,
}

export enum NetComponent {
  NetIncome = 'netIncome',
  PnL = 'profitAndLoss',
}

export type MyStocksContent = {
  id: string;
  name: string;
  icon: string;
  buttons: string;
  quantity: number;
  marketPrice: string;
  positionType: string;
  purchasePrice: string;
  unrealizedProfit: string;
  unrealizedProfitNumber: NullableNumber;
  subtitle: string | JSX.Element;
};

export type PortfolioChartElement = {
  x?: number;
  y?: number;
  symbol: string;
};

export type TradeHistoryParsedOrders = {
  aqxLastExecutionDate: string;
  color: string;
  expirationDate: string;
  lastUpdatedDate: string;
  orderDate: string;
  orderValue: string;
  pages: number;
  parentOrderId: string;
  price: string | number;
  quantity: string | number;
  rowNumber: number;
  tenNParsedStatus: string;
  statusEnum: SOROrderStatus;
  sorExecutedQuantity: number;
  symbol: string;
  timeInForce: SOROrderTimeInForce;
  type: string;
};

type APIResponse<T> = {
  hasLoaded: boolean;
  data: T;
};

export interface ReportingState {
  tradeHistoryOrders: TradeHistoryParsedOrders[];
  tradeHistoryNextPageToRead: number;
  pendingOrders: AccountTradeHistoryResponseData[];
  loadingExecutions: boolean;
  executionsFailed: boolean;
  accountOpenPositionsCompleted: boolean;
  executionsResult: IExecution[];
  accountsAvailableCash: APIResponse<GetAccountsAvailableCashResponse[]>;
  netIncomeComponents: GetMyAccountChartDataResponse;
  openPositions: OpenPosition[];
  leaderboard: LeaderboardResponse;
  accountSummary: AccountSummaryResponseData;
  orderDetail: OrderDetailResponse;
  orderDetailCompleted: boolean;
  error: RequestError;
  peopleAlsoTradeData: PeopleAlsoTradeItem[];
  fxTransactions: FxTransactionResponse;
  statusByCall: GenericStatusByCall<ReportingCall>;
  hasMyStocksOrderUpdate: boolean;
}

export type GetAccountsAvailableCashRequest = {
  accountsList: string[],
  messageType?: string,
}

export type AvailableCashElement = {
  accountReferenceId: string;
  availableCash: number;
  baseCurrency: CurrencyEnum | '';
  currency: CurrencyEnum | '';
  instaCash: number;
  isArchivingActive: boolean;
  selectedCurrency: CurrencyEnum | null;
}

export type AvailableCash = {
  availableCashMultiCurrency: AvailableCashElement[];
  totalInBaseCurrency: AvailableCashElement;
}

export type FXRates = {
  currencyFrom: CurrencyEnum;
  currencyTo: CurrencyEnum;
  rate: number;
}

export type GetAccountsAvailableCashResponse = {
  availableCash: AvailableCash[];
  fxRates: FXRates[];
};

export type GetMyAccountChartDataResponse = MyAccountChartDataResponse[];
export type GetOpenPositionsDataRequest = { symbol?: string, positionType?: string }

export type GetOpenPositionsDataResponse = {
  accounts: {
    accountReferenceId: string;
    openPositions: {
      averageOpeningPrice: number;
      cost: number;
      currency: string;
      lastExecutionDateTime: string;
      positionType: string;
      quantity: number;
      realizedPnl: number;
      symbol: string;
    }[];
  }[];
};

export type RequestError = {
  status: NullableNumber;
  hasRequestError: boolean;
};

interface SimplePageableRequest {
  pageNumber: number;
  pageSize: number;
}

export interface LeaderboardRequest extends SimplePageableRequest {
  planReferenceId: string;
  accountReferenceList: string;
  closestCompetitor: number;
}

export interface LeaderboardResponse extends SimplePageableRequest {
  items: number;
  tradePlan: LeaderboardTradePlan;
  competitors: LeaderboardCompetitor[];
  requestedCompetitors: LeaderboardRequestedCompetitor[];
}

export interface LeaderboardTradePlan {
  planId: number;
  planReferenceId: string;
  tradingPlanStatus: number;
  planType: number;
  planTypeName: string;
  planSubType: number;
  planSubTypeName: string;
  planName: string;
  planPurchasePeriodStart: string;
  planPurchasePeriodEnd: string;
  plansAccountsActivation: string;
  plansAccountsDeactivation: string;
  prizeType: boolean;
  prizeCCY: string;
  prizeOfAccumulatedTable: number;
  prizeMin: number;
  prizeMax: number;
  fixPrize: number;
}

export interface LeaderboardCompetitor {
  rank: number;
  sharingRank: number;
  tradingPlanId: number;
  accountReferenceId: string;
  profileName: string;
  firstName: string;
  lastName: string;
  netEquity: number;
  pnL: number;
  prize: number;
  currency: string;
}

export interface LeaderboardRequestedCompetitor extends LeaderboardCompetitor{
  requestedAccountReferenceId: string;
}
